import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import Pagination from '../components/Pagination';
import { Filter, Calendar, Download } from 'lucide-react';

const Purchases: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterStatus, setFilterStatus] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Sample data
    const purchases = [
        {
            id: 1,
            templateName: 'E-commerce Pro',
            amount: 59.99,
            status: 'Completed',
            paymentMethod: 'PayPal',
            licenseId: 'LIC-12345',
            serviceId: null,
            createdAt: '2025-03-01',
            customer: {
                name: 'John Smith',
                email: 'john.smith@example.com',
            },
        },
        {
            id: 2,
            templateName: 'Blog Master',
            amount: 39.99,
            status: 'Completed',
            paymentMethod: 'PayPal',
            licenseId: 'LIC-67890',
            serviceId: 'SRV-12345',
            createdAt: '2025-03-05',
            customer: {
                name: 'Sarah Johnson',
                email: 'sarah.johnson@example.com',
            },
        },
        {
            id: 3,
            templateName: 'Portfolio Plus',
            amount: 49.99,
            status: 'Pending',
            paymentMethod: 'PayPal',
            licenseId: null,
            serviceId: null,
            createdAt: '2025-03-08',
            customer: {
                name: 'Michael Brown',
                email: 'michael.brown@example.com',
            },
        },
        {
            id: 4,
            templateName: 'Landing Page Pro',
            amount: 29.99,
            status: 'Failed',
            paymentMethod: 'PayPal',
            licenseId: null,
            serviceId: null,
            createdAt: '2025-03-10',
            customer: {
                name: 'Emily Davis',
                email: 'emily.davis@example.com',
            },
        },
        {
            id: 5,
            templateName: 'Admin Dashboard',
            amount: 79.99,
            status: 'Completed',
            paymentMethod: 'PayPal',
            licenseId: 'LIC-24680',
            serviceId: 'SRV-67890',
            createdAt: '2025-03-15',
            customer: {
                name: 'David Wilson',
                email: 'david.wilson@example.com',
            },
        },
        {
            id: 6,
            templateName: 'E-commerce Pro',
            amount: 59.99,
            status: 'Completed',
            paymentMethod: 'PayPal',
            licenseId: 'LIC-13579',
            serviceId: null,
            createdAt: '2025-03-18',
            customer: {
                name: 'Jennifer Lee',
                email: 'jennifer.lee@example.com',
            },
        },
        {
            id: 7,
            templateName: 'Portfolio Plus',
            amount: 49.99,
            status: 'Completed',
            paymentMethod: 'PayPal',
            licenseId: 'LIC-97531',
            serviceId: null,
            createdAt: '2025-03-20',
            customer: {
                name: 'Robert Taylor',
                email: 'robert.taylor@example.com',
            },
        },
    ];

    const statusColors = {
        'Completed': 'success',
        'Pending': 'warning',
        'Failed': 'danger',
    } as const;

    // Filter and search
    const filteredPurchases = purchases.filter((purchase) => {
        const matchesSearch =
            purchase.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchase.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchase.customer.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || purchase.status === filterStatus;

        const purchaseDate = new Date(purchase.createdAt);
        const matchesStartDate = !startDate || new Date(startDate) <= purchaseDate;
        const matchesEndDate = !endDate || new Date(endDate) >= purchaseDate;

        return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate;
    });

    // Pagination
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
    const paginatedPurchases = filteredPurchases.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Calculate total revenue
    const totalRevenue = filteredPurchases.reduce((sum, purchase) => {
        return purchase.status === 'Completed' ? sum + purchase.amount : sum;
    }, 0);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Purchases</h1>
                <p className="mt-1 text-sm text-gray-500">
                    View and manage customer purchases
                </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-medium text-gray-900">Purchase Summary</h2>
                        <p className="mt-1 text-3xl font-semibold text-gray-900">
                            ${totalRevenue.toFixed(2)}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                            Total revenue from {filteredPurchases.filter(p => p.status === 'Completed').length} completed purchases
                        </p>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Button
                            variant="primary"
                            className="flex items-center"
                            onClick={() => {
                                // Export logic would go here
                                alert('Exporting purchase data...');
                            }}
                        >
                            <Download size={16} className="mr-2" />
                            Export Data
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <SearchInput
                    placeholder="Search purchases..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center space-x-2">
                        <Calendar size={20} className="text-gray-400" />
                        <div className="flex space-x-2">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                placeholder="Start Date"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                placeholder="End Date"
                            />
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Filter size={20} className="text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Completed">Completed</option>
                            <option value="Pending">Pending</option>
                            <option value="Failed">Failed</option>
                        </select>
                    </div>
                </div>
            </div>

            <Card>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Purchase ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Template
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                License/Service
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedPurchases.length > 0 ? (
                            paginatedPurchases.map((purchase) => (
                                <tr key={purchase.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        #{purchase.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {purchase.templateName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div>{purchase.customer.name}</div>
                                        <div className="text-xs text-gray-400">{purchase.customer.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        ${purchase.amount.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Badge
                                            variant={statusColors[purchase.status as keyof typeof statusColors]}
                                        >
                                            {purchase.status}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {purchase.createdAt}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {purchase.licenseId && (
                                            <div>License: {purchase.licenseId}</div>
                                        )}
                                        {purchase.serviceId && (
                                            <div>Service: {purchase.serviceId}</div>
                                        )}
                                        {!purchase.licenseId && !purchase.serviceId && (
                                            <span>-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                                // View details logic would go here
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                                    No purchases found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {filteredPurchases.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default Purchases;